// ─── Background Stream Manager ──────────────────────────────
// Manages persistent SSE spot-price streams for trailing stop engines.
// Streams survive React re-renders and page navigation — they only
// stop when explicitly released or the browser tab closes.
// All spot ticks are published to the global CtraderPriceBus.

import { supabase } from '@/lib/supabase';
import { ctraderPriceBus, type SpotTick } from '@/lib/ctrader-price-bus';
import { copyTradeExecutionBus } from '@/lib/copy-trade-execution-bus';
import { copyTradePositionsBus } from '@/lib/copy-trade-positions-bus';
import { subscribeSharedStream, type StreamEvent } from '@/lib/shared-ctrader-stream';

// ─── Types ──────────────────────────────────────────────────

interface ActiveStream {
  accountId: number;
  symbolIds: number[];
  symbolDigits: Record<number, number>;
  refCount: number;
  unsubscribe: (() => void) | null;
  running: boolean;
}

// ─── Module-level state ─────────────────────────────────────

const streams = new Map<number, ActiveStream>();
let streamManagerInitialized = false;

// ─── Start/stop a single stream ──────────────────────────────

function startStream(stream: ActiveStream): void {
  if (stream.running) return;

  // Unsubscribe previous
  if (stream.unsubscribe) {
    stream.unsubscribe();
    stream.unsubscribe = null;
  }

  stream.running = true;

  console.log(
    `[BackgroundStream] ➕ Subscribing via SharedStream — accountId=${stream.accountId} symbols=${stream.symbolIds.length}`
  );

  stream.unsubscribe = subscribeSharedStream(
    {
      id: `bg__${stream.accountId}`,
      symbolIds: stream.symbolIds,
      periods: [], // spots only for trailing stops
      symbolDigits: stream.symbolDigits,
      onEvent: (evt: StreamEvent) => {
        if (evt.type === 'spot' && evt.symbolId !== null && evt.symbolId !== undefined) {
          ctraderPriceBus.publish({
            symbolId: evt.symbolId,
            bid: evt.bid ?? null,
            ask: evt.ask ?? null,
            timestamp: evt.receivedAt || Date.now(),
          });
        }
        if (evt.type === 'execution') {
          copyTradeExecutionBus.publish({
            type: 'execution',
            accountId: stream.accountId,
            payload: evt.payload as Record<string, unknown>,
            timestamp: evt.receivedAt || Date.now(),
          });
        }
        if (evt.type === 'positions') {
          copyTradePositionsBus.publish({
            accountId: stream.accountId,
            reconcileRes: evt.reconcileRes || {},
            symbolRes: evt.symbolRes || null,
            positionCount: evt.positionCount || 0,
            timestamp: evt.receivedAt || Date.now(),
          });
        }
        if (evt.type === 'error') {
          console.warn(`[BackgroundStream] Error for account ${stream.accountId}:`, evt.description || evt.message);
        }
      },
    },
    stream.accountId,
  );
}

function stopStream(stream: ActiveStream): void {
  if (stream.unsubscribe) {
    stream.unsubscribe();
    stream.unsubscribe = null;
  }
  stream.running = false;
}

// ─── Public API ─────────────────────────────────────────────

/**
 * Ensure a spot-price SSE stream is running for the given account.
 * Ref-counted: multiple callers can call ensureStream for the same account.
 * Stream only stops when releaseStream is called an equal number of times.
 */
export function ensureStream(
  accountId: number,
  symbolIds: number[],
  symbolDigits: Record<number, number> = {},
): void {
  let stream = streams.get(accountId);

  if (stream) {
    stream.refCount++;
    // Merge new symbol IDs (don't restart stream, just update the set)
    const existing = new Set(stream.symbolIds);
    let changed = false;
    for (const sid of symbolIds) {
      if (!existing.has(sid)) {
        existing.add(sid);
        changed = true;
      }
    }
    if (changed) {
      stream.symbolIds = [...existing];
      // Restart with updated symbol list
      stopStream(stream);
      startStream(stream);
    }
    return;
  }

  stream = {
    accountId,
    symbolIds: [...symbolIds],
    symbolDigits: { ...symbolDigits },
    refCount: 1,
    unsubscribe: null,
    running: false,
  };

  streams.set(accountId, stream);
  startStream(stream);
}

/**
 * Release a reference to a stream. When refCount hits 0, the stream stops.
 */
export function releaseStream(accountId: number): void {
  const stream = streams.get(accountId);
  if (!stream) return;

  stream.refCount--;
  if (stream.refCount <= 0) {
    stopStream(stream);
    streams.delete(accountId);
    console.log(`[BackgroundStream] Released account ${accountId}`);
  }
}

/**
 * Update stream symbols without changing ref count.
 * Called when engine detects new positions with new symbols.
 */
export function updateStreamSymbols(
  accountId: number,
  symbolIds: number[],
  symbolDigits: Record<number, number> = {},
): void {
  const stream = streams.get(accountId);
  if (!stream) {
    // Stream doesn't exist yet — create it
    ensureStream(accountId, symbolIds, symbolDigits);
    return;
  }

  // Idempotent: only restart when the symbol set actually changed. The
  // copy-trade engine now receives a positions snapshot on every reconcile
  // (every few seconds), so without this guard the stream would restart
  // constantly even though the symbol list is unchanged.
  const sameSymbols =
    stream.symbolIds.length === symbolIds.length &&
    stream.symbolIds.every((s) => symbolIds.includes(s));

  stream.symbolDigits = { ...symbolDigits };

  if (sameSymbols) {
    return;
  }

  stream.symbolIds = [...symbolIds];
  // Restart to apply new symbol list
  stopStream(stream);
  startStream(stream);
}